import { CheckboxGroup } from "@base-ui/react/checkbox-group";
import { Collapsible } from "@base-ui/react/collapsible";
import { cn } from "@beweegbaas/ui/lib/utils";
import * as React from "react";
import IconChevronRight from "~icons/material-symbols/chevron-right-rounded";

import { Checkbox } from "./checkbox";
import { Label } from "./label";

export interface TreeItem {
  id: string;
  name: string;
  children?: TreeItem[];
  visible?: boolean;
}

export interface TreeProps {
  items: TreeItem[];
  value?: string[];
  onValueChange?: (values: string[]) => void;
  renderItem?: (item: TreeItem, isSelected: boolean) => React.ReactNode;
  className?: string;
  "aria-label"?: string;
}

// Helper to collect all IDs from a list of items recursively
function collectAllIds(items: TreeItem[]): string[] {
  const ids: string[] = [];
  items.forEach((item) => {
    ids.push(item.id);
    if (item.children) {
      ids.push(...collectAllIds(item.children));
    }
  });
  return ids;
}

// Helper to check if the tree is flat (only one level, no children)
function isFlat(items: TreeItem[]): boolean {
  return !items.some((item) => item.children && item.children.length > 0);
}

interface TreeItemGroupProps {
  item: TreeItem;
  level: number;
  selectedValues: string[];
  onValueChange: (values: string[]) => void;
  renderItem?: (item: TreeItem, isSelected: boolean) => React.ReactNode;
  isFlat: boolean;
}

function TreeItemGroup({
  item,
  level,
  selectedValues,
  onValueChange,
  renderItem,
  isFlat,
}: TreeItemGroupProps) {
  const hasChildren = item.children && item.children.length > 0;
  const isSelected = selectedValues.includes(item.id);

  // Collect all child IDs for this group (empty array if no children)
  const allChildIds = hasChildren && item.children ? collectAllIds(item.children) : [];
  const groupAllValues = [item.id, ...allChildIds];
  const groupValues = selectedValues.filter((id) => groupAllValues.includes(id));

  const handleGroupValueChange = React.useCallback(
    (newGroupValues: string[]) => {
      // Merge new group values with values outside this group
      const valuesOutsideGroup = selectedValues.filter((id) => !groupAllValues.includes(id));
      const mergedValues = [...valuesOutsideGroup, ...newGroupValues];
      onValueChange(mergedValues);
    },
    [selectedValues, groupAllValues, onValueChange],
  );

  const row = (
    <div
      className={cn(
        "flex items-center gap-2",
        "hover:bg-muted squircle rounded px-1 py-1.5",
        "supports-squircle:rounded-full",
        isSelected && "bg-blue-100 hover:bg-blue-200 dark:bg-blue-950/60 dark:hover:bg-blue-950",
      )}
      style={{
        marginLeft: `${level * 8}px`,
        paddingLeft: `${level * 8 + 8}px`,
      }}
    >
      {hasChildren ? (
        <Collapsible.Trigger className="group/tree-trigger flex items-center">
          <IconChevronRight
            className="size-4 transition-transform
              group-data-panel-open/tree-trigger:rotate-90"
          />
        </Collapsible.Trigger>
      ) : !isFlat ? (
        <span className="inline-block size-4" />
      ) : null}
      <Label className="flex-1">
        <Checkbox value={item.id} {...(hasChildren ? { parent: true } : {})} />
        {renderItem ? renderItem(item, isSelected) : item.name}
      </Label>
    </div>
  );

  return (
    <CheckboxGroup
      value={groupValues}
      onValueChange={handleGroupValueChange}
      allValues={groupAllValues}
      className="flex flex-col gap-0.5"
    >
      {hasChildren && item.children ? (
        <Collapsible.Root className="flex flex-col gap-0.5">
          {row}
          <Collapsible.Panel
            className="flex h-(--collapsible-panel-height) flex-col gap-0.5
              overflow-hidden transition-[height] duration-150 ease-out
              data-ending-style:h-0 data-starting-style:h-0"
          >
            {item.children.map((child) => (
              <TreeItemGroup
                key={child.id}
                item={child}
                level={level + 1}
                selectedValues={selectedValues}
                onValueChange={onValueChange}
                renderItem={renderItem}
                isFlat={isFlat}
              />
            ))}
          </Collapsible.Panel>
        </Collapsible.Root>
      ) : (
        row
      )}
    </CheckboxGroup>
  );
}

export function Tree({
  items,
  value = [],
  onValueChange,
  renderItem,
  className,
  "aria-label": ariaLabel = "Tree",
}: TreeProps) {
  // Filter visible items
  const visibleItems = React.useMemo(() => {
    const filterVisible = (items: TreeItem[]): TreeItem[] => {
      return items
        .filter((item) => item.visible !== false)
        .map((item) => ({
          ...item,
          children: item.children ? filterVisible(item.children) : [],
        }));
    };
    return filterVisible(items);
  }, [items]);

  // Collect all possible IDs for the root CheckboxGroup
  const allIds = collectAllIds(visibleItems);

  // Check if the tree is flat (only one level, no children)
  const treeIsFlat = React.useMemo(() => isFlat(visibleItems), [visibleItems]);

  const handleValueChange = React.useCallback(
    (newValue: string[]) => {
      onValueChange?.(newValue);
    },
    [onValueChange],
  );

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <CheckboxGroup
      value={value}
      onValueChange={handleValueChange}
      allValues={allIds}
      className={cn("flex flex-col gap-0.5", className)}
      aria-label={ariaLabel}
    >
      {visibleItems.map((item) => (
        <TreeItemGroup
          key={item.id}
          item={item}
          level={0}
          selectedValues={value}
          onValueChange={handleValueChange}
          renderItem={renderItem}
          isFlat={treeIsFlat}
        />
      ))}
    </CheckboxGroup>
  );
}
